/**
 * ChangePasswordPage — lets a logged-in user change their password.
 *
 * Used in two flows:
 *   1. First-login for beta-approved users who still have their
 *      temporary password. Dashboard redirects them here until the
 *      `must_change_password` flag is cleared.
 *   2. Self-service from Settings for anyone who wants a new password.
 */
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle2, KeyRound } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

const MIN_LEN = 10

function scorePassword(pw: string): { score: number; label: string; color: string } {
  let score = 0
  if (pw.length >= MIN_LEN) score++
  if (pw.length >= 14) score++
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++
  if (/\d/.test(pw)) score++
  if (/[^a-zA-Z0-9]/.test(pw)) score++
  const labels = ['Too short', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent']
  const colors = ['rose', 'rose', 'amber', 'amber', 'emerald', 'emerald']
  return { score, label: labels[score], color: colors[score] }
}

export default function ChangePasswordPage() {
  const navigate = useNavigate()
  const user = useAuth(s => s.user)
  const changePassword = useAuth(s => s.changePassword)
  const loading = useAuth(s => s.loading)

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNext, setShowNext] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const isForced = user?.must_change_password === true
  const strength = scorePassword(next)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (next.length < MIN_LEN) {
      setError(`New password must be at least ${MIN_LEN} characters`)
      return
    }
    if (next !== confirm) {
      setError('New passwords do not match')
      return
    }
    if (next === current) {
      setError('New password must be different from current password')
      return
    }

    try {
      await changePassword(current, next)
      setDone(true)
      // Auto-redirect after success
      setTimeout(() => navigate('/app', { replace: true }), 1200)
    } catch (err: any) {
      setError(err?.message ?? 'Could not change password')
    }
  }

  if (done) {
    return (
      <div className="max-w-md mx-auto mt-10">
        <div className="card p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-emerald/10 border border-emerald/30 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={24} className="text-emerald" aria-hidden />
          </div>
          <h1 className="text-base font-bold text-text-primary">Password updated</h1>
          <p className="text-sm text-text-secondary mt-1">
            Redirecting you to the dashboard…
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto mt-6 space-y-4 animate-fade-in">
      {isForced && (
        <div className="card p-4 border-amber/40 bg-amber/5 flex items-start gap-3">
          <KeyRound size={16} className="text-amber shrink-0 mt-0.5" aria-hidden />
          <div>
            <p className="text-sm font-semibold text-text-primary">
              Choose a new password
            </p>
            <p className="text-xs text-text-muted mt-0.5">
              You're signed in with a temporary password. Pick a new one to finish
              setting up your account.
            </p>
          </div>
        </div>
      )}

      <div className="card p-6">
        <div className="flex items-center gap-2 mb-5">
          <Lock size={18} className="text-blue-light" aria-hidden />
          <h1 className="text-base font-bold text-text-primary">Change password</h1>
        </div>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-rose/10 border border-rose/20 text-rose-light text-sm"
          >
            <AlertCircle size={14} className="shrink-0 mt-0.5" aria-hidden />
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4" noValidate>
          {/* Current */}
          <div>
            <label htmlFor="pw-current" className="block text-xs font-medium text-text-secondary mb-1">
              Current password
            </label>
            <div className="relative">
              <input
                id="pw-current"
                type={showCurrent ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={current}
                onChange={e => setCurrent(e.target.value)}
                className="input pr-10"
                placeholder={isForced ? 'Your temporary password' : 'Your current password'}
              />
              <button
                type="button"
                onClick={() => setShowCurrent(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
                aria-label={showCurrent ? 'Hide password' : 'Show password'}
              >
                {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          {/* New */}
          <div>
            <label htmlFor="pw-new" className="block text-xs font-medium text-text-secondary mb-1">
              New password
            </label>
            <div className="relative">
              <input
                id="pw-new"
                type={showNext ? 'text' : 'password'}
                autoComplete="new-password"
                required
                minLength={MIN_LEN}
                value={next}
                onChange={e => setNext(e.target.value)}
                className="input pr-10"
                placeholder={`At least ${MIN_LEN} characters`}
              />
              <button
                type="button"
                onClick={() => setShowNext(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
                aria-label={showNext ? 'Hide password' : 'Show password'}
              >
                {showNext ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            {next.length > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-surface-hover rounded-full overflow-hidden flex gap-0.5">
                  {[0, 1, 2, 3, 4].map(i => (
                    <div
                      key={i}
                      className={`flex-1 rounded-full transition-colors ${
                        i < strength.score
                          ? strength.color === 'emerald'
                            ? 'bg-emerald'
                            : strength.color === 'amber'
                            ? 'bg-amber'
                            : 'bg-rose'
                          : ''
                      }`}
                    />
                  ))}
                </div>
                <span
                  className={`text-[10px] font-medium ${
                    strength.color === 'emerald'
                      ? 'text-emerald'
                      : strength.color === 'amber'
                      ? 'text-amber'
                      : 'text-rose-light'
                  }`}
                >
                  {strength.label}
                </span>
              </div>
            )}
          </div>

          {/* Confirm */}
          <div>
            <label htmlFor="pw-confirm" className="block text-xs font-medium text-text-secondary mb-1">
              Confirm new password
            </label>
            <input
              id="pw-confirm"
              type={showNext ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              className="input"
              placeholder="Type it again"
            />
            {confirm.length > 0 && confirm !== next && (
              <p className="text-[10px] text-rose-light mt-1">Passwords don't match</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !current || !next || !confirm}
            className="btn-primary w-full"
          >
            {loading ? 'Updating…' : 'Change password'}
          </button>
        </form>

        {!isForced && (
          <p className="mt-4 text-center text-[10px] text-text-muted">
            You'll stay signed in after the change.
          </p>
        )}
      </div>
    </div>
  )
}
